import React from 'react'
import { Link } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { Avatar, AvatarImage, AvatarFallback } from 'components/ui/avatar'
import { GitBranch } from 'lucide-react'

function formatActivityDate(dateStr) {
	if (!dateStr) return ''
	const d = new Date(dateStr)
	const now = new Date()
	const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate())
	const sevenDaysAgo = new Date(todayStart)
	sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7)

	if (d >= todayStart) {
		return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true })
	}
	if (d >= sevenDaysAgo) {
		return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })
	}
	return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function SidebarProjectsRecent() {
	const { projects, isLoading } = useSelector(state => {
		const projectIds = state.projects.recents
		const myRecentProjects = projectIds?.map(c => state.projects.entities[c])
		return {
			projects: myRecentProjects || [],
			isLoading: state.projects.isLoading,
		}
	})

	const isEmpty = projects?.length === 0

	return (
		<div className="flex flex-col h-full">
			<div className="px-4 py-3 border-b border-gray-100">
				<div className="flex items-center gap-2">
					<svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
					</svg>
					<span className="text-sm font-semibold text-gray-900">Recent Activity</span>
					{projects.length > 0 && (
						<span className="text-xs px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">
							{projects.length}
						</span>
					)}
				</div>
			</div>
			<div className="flex-1 overflow-y-auto">
				{isLoading ? (
					<div className="flex items-center justify-center h-full p-4">
						<span className="text-sm text-gray-400">Loading...</span>
					</div>
				) : isEmpty ? (
					<div className="text-center py-6">
						<svg className="w-5 h-5 mx-auto text-gray-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
						</svg>
						<p className="text-sm text-gray-400">No activity yet</p>
					</div>
				) : (
					<div className="flow-root px-4 pt-3">
						<ul className="-mb-6">
							{projects.map((project, idx) => {
								const isLast = idx === projects.length - 1
								const assetName = project.projectAsset?.assetName
								return (
									<li key={project._id || idx} className="relative pb-6 last:pb-0">
										{!isLast && (
											<span
												className="absolute left-[15px] top-8 -ml-px h-full w-0.5 bg-gray-200"
												aria-hidden="true"
											/>
										)}
										<Link
											to={`/locations/${project.projectAsset?._id}/projects/${project._id}`}
											className="relative flex items-start gap-3 no-underline group"
										>
											<Avatar className="h-8 w-8 flex-shrink-0">
												{project.projectAsset?.image ? (
													<>
														<AvatarImage src={project.projectAsset.image} alt={project.projectName} />
														<AvatarFallback className="text-xs bg-gray-200 text-gray-600">
															{(project.projectName || '?').charAt(0).toUpperCase()}
														</AvatarFallback>
													</>
												) : (
													<AvatarFallback className="bg-blue-50 text-blue-600">
														<GitBranch className="w-3.5 h-3.5" />
													</AvatarFallback>
												)}
											</Avatar>
											<div className="min-w-0 flex-1 pt-0.5">
												<span className="text-sm font-medium text-gray-900 group-hover:text-[#0080fe] transition-colors">
													{project.projectName}
												</span>
												{assetName && (
													<p className="mt-0.5 text-xs text-gray-400">{assetName}</p>
												)}
												{project.updateNote && (
													<p className="mt-0.5 text-sm text-gray-500">{project.updateNote}</p>
												)}
												{project.updatedAt && (
													<p className="mt-0.5 text-xs text-gray-400">{formatActivityDate(project.updatedAt)}</p>
												)}
											</div>
										</Link>
									</li>
								)
							})}
						</ul>
					</div>
				)}
			</div>
		</div>
	)
}

export default SidebarProjectsRecent
